import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import apiRequest, { get, post, put } from "../../Services/ApiRequest";
import { API_URLS } from "../../Utils/Constants/APIConstants";
import { toast } from "../../Utils/Helper/toast";

const toErrorMessage = (value: any, fallback: string): string => {
  if (typeof value === "string") return value;
  if (value?.message && typeof value.message === "string") return value.message;
  if (value?.detail && typeof value.detail === "string") return value.detail;
  if (Array.isArray(value?.detail)) {
    return value.detail
      .map((item: any) =>
        typeof item === "string"
          ? item
          : item?.msg || item?.message || JSON.stringify(item)
      )
      .join(", ");
  }
  if (Array.isArray(value)) {
    return value
      .map((item: any) =>
        typeof item === "string" ? item : item?.msg || JSON.stringify(item)
      )
      .join(", ");
  }
  if (value && typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return fallback;
    }
  }
  return fallback;
};

export interface MetricsCsvImportPayload {
  file: File;
  batchName: string;
}

export interface MetricsCsvValidateConflict {
  row: number;
  account_id?: string | null;
  account_name?: string | null;
  metric_id: string;
  metric_name?: string | null;
  measurement_date?: string | null;
  csv_value?: string | null;
  exists_in_staging: boolean;
  exists_in_production: boolean;
  staging_value?: string | null;
  production_value?: string | null;
  warnings: string[];
}

export interface MetricsCsvValidateResult {
  success: boolean;
  total_rows: number;
  valid_rows: number;
  error_rows: number;
  duplicate_rows_in_file: number;
  staging_conflict_count: number;
  production_conflict_count: number;
  both_conflict_count: number;
  has_conflicts: boolean;
  validation_errors: Array<Record<string, unknown>>;
  conflicts: MetricsCsvValidateConflict[];
  processing_time_ms: number;
}

export interface MetricsStagingTransferPayload {
  batch_id?: string | number;
  organization_id?: string;
  organization_name?: string;
}

export interface MetricsBenchmarkCalculatePayload {
  timeWindowMonths?: number;
  minDataPoints?: number;
  minUniqueAccounts?: number;
}

export interface MetricsBenchmarkPropagatePayload {
  timeWindowMonths?: number;
}

export interface MetricsNormalizePayload {
  maxRecords?: number;
  batchSize?: number;
  onlyNullNormalized?: boolean;
}

export interface MetricsSummaryStatusPayload {
  is_stale: boolean;
  stats: {
    timeseries_rows: number;
    distinct_metrics: number;
    distinct_dates: number;
    distinct_accounts: number;
    summary_rows: number;
    metrics_with_summaries: number;
    metrics_without_summaries: number;
  };
  progress: any | null;
}

export interface MetricsOrgConfigurationUpdatePayload {
  configuration_id: string;
  template_name: string;
  config_version: number;
  config: {
    is_master?: boolean;
    /** When true, skip success toast (e.g. synthetic/live toggle). */
    silent?: boolean;
    /** Canonical Always On / Hawkeye lane flag (live vs synthetic). */
    use_synthetic_data?: boolean;
    categories?: Array<{
      name: string;
      weight: number;
      enabled: boolean;
      metrics: Array<{
        name: string;
        weight: number;
        enabled: boolean;
        cadence?: string;
        coverage?: number | string;
        metric_status?: string;
        last_ingested?: string;
        coverage_detail?: Record<string, unknown>;
      }>;
    }>;
    /** Legacy nested shape still written by some MDM screens. */
    config_override?: {
      use_synthetic_data?: boolean;
      categories?: Array<{
        name: string;
        weight: number;
        enabled: boolean;
        metrics: Array<{
          name: string;
          weight: number;
          enabled: boolean;
          cadence?: string;
          coverage?: number | string;
          metric_status?: string;
          last_ingested?: string;
          coverage_detail?: Record<string, unknown>;
        }>;
      }>;
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
}
interface MetricsState {
  csvValidate: {
    loading: boolean;
    error: string | null;
    success: boolean;
    data: MetricsCsvValidateResult | null;
  };
  csvImport: {
    loading: boolean;
    error: string | null;
    success: boolean;
    data: any | null;
  };
  stagingTransfer: {
    loading: boolean;
    error: string | null;
    success: boolean;
    data: any | null;
  };
  stagingCount: {
    loading: boolean;
    error: string | null;
    success: boolean;
    count: number;
  };
  benchmarkCalculate: {
    loading: boolean;
    error: string | null;
    success: boolean;
    data: any | null;
  };
  benchmarkPropagate: {
    loading: boolean;
    error: string | null;
    success: boolean;
    data: any | null;
  };
  normalization: {
    loading: boolean;
    error: string | null;
    success: boolean;
    data: any | null;
  };
  normalizationPendingCount: {
    loading: boolean;
    error: string | null;
    success: boolean;
    count: number;
  };
  orgConfiguration: {
    loading: boolean;
    error: string | null;
    success: boolean;
    data: any | null;
  };
  orgConfigurationUpdate: {
    loading: boolean;
    error: string | null;
    success: boolean;
  };
  summaryStatus: {
    loading: boolean;
    error: string | null;
    success: boolean;
    data: MetricsSummaryStatusPayload | null;
  };
  summaryTransfer: {
    loading: boolean;
    error: string | null;
    success: boolean;
    data: any | null;
  };
  timePeriods: {
    loading: boolean;
    error: string | null;
    success: boolean;
    data: any | null;
  };
  coverageAudit: {
    loading: boolean;
    error: string | null;
    success: boolean;
    data: any | null;
  };
  hawkeyeDigest: {
    loading: boolean;
    error: string | null;
    success: boolean;
    data: any | null;
  };
  adminDatasets: {
    loading: boolean;
    error: string | null;
    success: boolean;
    data: any | null;
  };
}

const initialState: MetricsState = {
  csvValidate: {
    loading: false,
    error: null,
    success: false,
    data: null,
  },
  csvImport: {
    loading: false,
    error: null,
    success: false,
    data: null,
  },
  stagingTransfer: {
    loading: false,
    error: null,
    success: false,
    data: null,
  },
  stagingCount: {
    loading: false,
    error: null,
    success: false,
    count: 0,
  },
  benchmarkCalculate: {
    loading: false,
    error: null,
    success: false,
    data: null,
  },
  benchmarkPropagate: {
    loading: false,
    error: null,
    success: false,
    data: null,
  },
  normalization: {
    loading: false,
    error: null,
    success: false,
    data: null,
  },
  normalizationPendingCount: {
    loading: false,
    error: null,
    success: false,
    count: 0,
  },
  orgConfiguration: {
    loading: false,
    error: null,
    success: false,
    data: null,
  },
  orgConfigurationUpdate: {
    loading: false,
    error: null,
    success: false,
  },
  summaryStatus: {
    loading: false,
    error: null,
    success: false,
    data: null,
  },
  summaryTransfer: {
    loading: false,
    error: null,
    success: false,
    data: null,
  },
  timePeriods: {
    loading: false,
    error: null,
    success: false,
    data: null,
  },
  coverageAudit: {
    loading: false,
    error: null,
    success: false,
    data: null,
  },
  hawkeyeDigest: {
    loading: false,
    error: null,
    success: false,
    data: null,
  },
  adminDatasets: {
    loading: false,
    error: null,
    success: false,
    data: null,
  },
};

export const validateMetricsCsv = createAsyncThunk(
  "metrics/validateCsv",
  async (file: File, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await apiRequest(
        "POST",
        API_URLS.metricsImportCsvValidate(),
        formData as any,
        false,
        true
      );

      return (response?.content ?? response) as MetricsCsvValidateResult;
    } catch (error: any) {
      let errorMessage = "Failed to validate metrics CSV";
      try {
        const parsedError = JSON.parse(error?.message || "{}");
        errorMessage = toErrorMessage(
          parsedError?.message || error?.response?.data || error?.message,
          errorMessage
        );
      } catch {
        errorMessage = toErrorMessage(
          error?.response?.data || error?.message,
          errorMessage
        );
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const importMetricsCsv = createAsyncThunk(
  "metrics/importCsv",
  async (payload: MetricsCsvImportPayload, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", payload.file);
      formData.append("batch_name", payload.batchName);

      const importResponse = await apiRequest(
        "POST",
        API_URLS.metricsImportCsv(),
        formData as any,
        false,
        true
      );

      const batchId =
        importResponse?.content?.batch_id ?? importResponse?.content?.batchId;

      if (!batchId) {
        return rejectWithValue(
          "Metrics CSV imported but batch_id is missing for staging process."
        );
      }

      const processFormData = new FormData();
      processFormData.append("file", payload.file);
      processFormData.append("batch_name", payload.batchName);

      const processResponse = await post(
        API_URLS.metricsStagingProcess(batchId),
        processFormData as any,
        false
      );

      return {
        importResponse,
        processResponse,
        batchId,
      };
    } catch (error: any) {
      let errorMessage = "Failed to import metrics CSV";
      try {
        const parsedError = JSON.parse(error?.message || "{}");
        errorMessage = toErrorMessage(
          parsedError?.message || error?.response?.data || error?.message,
          errorMessage
        );
      } catch {
        errorMessage = toErrorMessage(
          error?.response?.data || error?.message,
          errorMessage
        );
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const transferMetricsStagingToProduction = createAsyncThunk(
  "metrics/stagingTransfer",
  async (payload: MetricsStagingTransferPayload = {}, { rejectWithValue }) => {
    try {
      const response = await post(
        API_URLS.metricsStagingTransfer(payload),
        null,
        false
      );
      return response;
    } catch (error: any) {
      let errorMessage = "Failed to transfer staging data to production";
      try {
        const parsedError = JSON.parse(error?.message || "{}");
        errorMessage = toErrorMessage(
          parsedError?.message || error?.response?.data || error?.message,
          errorMessage
        );
      } catch {
        errorMessage = toErrorMessage(
          error?.response?.data || error?.message,
          errorMessage
        );
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const calculateMetricsBenchmarks = createAsyncThunk(
  "metrics/benchmarkCalculate",
  async (payload: MetricsBenchmarkCalculatePayload, { rejectWithValue }) => {
    try {
      const response = await post(
        API_URLS.metricsBenchmarksCalculate({
          time_window_months: payload.timeWindowMonths,
          min_data_points: payload.minDataPoints,
          min_unique_accounts: payload.minUniqueAccounts,
        }),
        null,
        false
      );
      return response;
    } catch (error: any) {
      let errorMessage = "Failed to calculate benchmarks";
      try {
        const parsedError = JSON.parse(error?.message || "{}");
        errorMessage = toErrorMessage(
          parsedError?.message || error?.response?.data || error?.message,
          errorMessage
        );
      } catch {
        errorMessage = toErrorMessage(
          error?.response?.data || error?.message,
          errorMessage
        );
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchMetricsStagingCount = createAsyncThunk(
  "metrics/stagingCount",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.metricsStagingCount());
      const count =
        response?.content?.count ??
        response?.count ??
        response?.data?.content?.count ??
        response?.data?.count ??
        0;
      return Number(count) || 0;
    } catch (error: any) {
      let errorMessage = "Failed to load staging count";
      try {
        const parsedError = JSON.parse(error?.message || "{}");
        errorMessage = toErrorMessage(
          parsedError?.message || error?.response?.data || error?.message,
          errorMessage
        );
      } catch {
        errorMessage = toErrorMessage(
          error?.response?.data || error?.message,
          errorMessage
        );
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchMetricsOrgConfiguration = createAsyncThunk(
  "metrics/fetchOrgConfiguration",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.metricsOrgConfigurations());
      const configuration =
        response?.content?.configuration ??
        response?.configuration ??
        response?.content ??
        null;
      // Prefer nested configuration object when content wraps it.
      if (
        configuration &&
        typeof configuration === "object" &&
        "configuration" in configuration &&
        (configuration as { configuration?: unknown }).configuration
      ) {
        return (configuration as { configuration: unknown }).configuration;
      }
      return configuration;
    } catch (error: any) {
      let errorMessage = "Failed to load metrics configuration";
      try {
        const parsedError = JSON.parse(error?.message || "{}");
        errorMessage = toErrorMessage(
          parsedError?.message || error?.response?.data || error?.message,
          errorMessage
        );
      } catch {
        errorMessage = toErrorMessage(
          error?.response?.data || error?.message,
          errorMessage
        );
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateMetricsOrgConfiguration = createAsyncThunk(
  "metrics/updateOrgConfiguration",
  async (
    payload: MetricsOrgConfigurationUpdatePayload,
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await put(
        API_URLS.metricsOrgConfigurationsUpdate(payload.configuration_id),
        {
          template_name: payload.template_name,
          config_version: payload.config_version,
          config: payload.config,
        }
      );
      await dispatch(fetchMetricsOrgConfiguration());
      return response;
    } catch (error: any) {
      let errorMessage = "Failed to update metrics configuration";
      try {
        const parsedError = JSON.parse(error?.message || "{}");
        errorMessage = toErrorMessage(
          parsedError?.message || error?.response?.data || error?.message,
          errorMessage
        );
      } catch {
        errorMessage = toErrorMessage(
          error?.response?.data || error?.message,
          errorMessage
        );
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const propagateMetricsBenchmarks = createAsyncThunk(
  "metrics/benchmarkPropagate",
  async (
    payload: MetricsBenchmarkPropagatePayload = {},
    { rejectWithValue }
  ) => {
    try {
      const response = await post(
        API_URLS.metricsBenchmarksPropagate({
          time_window_months: payload.timeWindowMonths ?? 6,
        }),
        null,
        false
      );
      return response;
    } catch (error: any) {
      let errorMessage = "Failed to propagate benchmarks";
      try {
        const parsedError = JSON.parse(error?.message || "{}");
        errorMessage = toErrorMessage(
          parsedError?.message || error?.response?.data || error?.message,
          errorMessage
        );
      } catch {
        errorMessage = toErrorMessage(
          error?.response?.data || error?.message,
          errorMessage
        );
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const normalizeMetrics = createAsyncThunk(
  "metrics/normalize",
  async (payload: MetricsNormalizePayload, { rejectWithValue }) => {
    try {
      const response = await post(
        API_URLS.metricsNormalize({
          max_records: payload.maxRecords ?? 10000,
          batch_size: payload.batchSize ?? 1000,
          only_null_normalized: payload.onlyNullNormalized ?? true,
        }),
        null,
        false
      );
      return response;
    } catch (error: any) {
      let errorMessage = "Failed to normalize metrics";
      try {
        const parsedError = JSON.parse(error?.message || "{}");
        errorMessage = toErrorMessage(
          parsedError?.message || error?.response?.data || error?.message,
          errorMessage
        );
      } catch {
        errorMessage = toErrorMessage(
          error?.response?.data || error?.message,
          errorMessage
        );
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchMetricsNormalizePendingCount = createAsyncThunk(
  "metrics/normalizePendingCount",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.metricsNormalizePendingCount());
      return Number(response?.content?.count ?? 0);
    } catch (error: any) {
      let errorMessage = "Failed to load pending normalization count";
      try {
        const parsedError = JSON.parse(error?.message || "{}");
        errorMessage = toErrorMessage(
          parsedError?.message || error?.response?.data || error?.message,
          errorMessage
        );
      } catch {
        errorMessage = toErrorMessage(
          error?.response?.data || error?.message,
          errorMessage
        );
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchMetricsSummaryStatus = createAsyncThunk(
  "metrics/summaryStatus",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.metricsSummaryStatus());
      return (
        response?.content ?? {
          is_stale: true,
          stats: {
            timeseries_rows: 0,
            distinct_metrics: 0,
            distinct_dates: 0,
            distinct_accounts: 0,
            summary_rows: 0,
            metrics_with_summaries: 0,
            metrics_without_summaries: 0,
          },
          progress: null,
        }
      );
    } catch (error: any) {
      let errorMessage = "Failed to load summary status";
      try {
        const parsedError = JSON.parse(error?.message || "{}");
        errorMessage = toErrorMessage(
          parsedError?.message || error?.response?.data || error?.message,
          errorMessage
        );
      } catch {
        errorMessage = toErrorMessage(
          error?.response?.data || error?.message,
          errorMessage
        );
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const transferMetricsSummary = createAsyncThunk(
  "metrics/summaryTransfer",
  async (_, { rejectWithValue }) => {
    try {
      const response = await post(
        API_URLS.metricsSummaryTransfer(),
        null,
        false
      );
      return response;
    } catch (error: any) {
      let errorMessage = "Failed to refresh metric summaries";
      try {
        const parsedError = JSON.parse(error?.message || "{}");
        errorMessage = toErrorMessage(
          parsedError?.message || error?.response?.data || error?.message,
          errorMessage
        );
      } catch {
        errorMessage = toErrorMessage(
          error?.response?.data || error?.message,
          errorMessage
        );
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export interface FetchMetricsTimePeriodsResult {
  min_measurement_date?: string | null;
  max_measurement_date?: string | null;
  min_created_date?: string | null;
  max_created_date?: string | null;
  [key: string]: unknown;
}

export const fetchMetricsTimePeriods = createAsyncThunk(
  "metrics/fetchTimePeriods",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.metricsTimePeriods());
      return (response?.content ?? response) as FetchMetricsTimePeriodsResult;
    } catch (error: any) {
      return rejectWithValue(
        toErrorMessage(
          error?.response?.data || error?.message,
          "Failed to load metrics time periods"
        )
      );
    }
  }
);

export const fetchMetricsConfigCoverageAudit = createAsyncThunk(
  "metrics/fetchConfigCoverageAudit",
  async (_, { rejectWithValue }) => {
    try {
      return await get(API_URLS.metricsConfigCoverageAudit());
    } catch (error: any) {
      return rejectWithValue(
        toErrorMessage(
          error?.response?.data || error?.message,
          "Failed to load data coverage audit"
        )
      );
    }
  }
);

export type FetchHawkeyeDigestPayload = {
  account_ids: string[];
  from_date?: string;
  to_date?: string;
};

export const fetchHawkeyeDigest = createAsyncThunk(
  "metrics/fetchHawkeyeDigest",
  async (payload: FetchHawkeyeDigestPayload, { rejectWithValue }) => {
    try {
      return await post(API_URLS.metricsHawkeyeDigestDb(), payload);
    } catch (error: any) {
      return rejectWithValue(
        toErrorMessage(
          error?.response?.data || error?.message,
          "Failed to load Hawkeye digests"
        )
      );
    }
  }
);

export const fetchAdminMetricsDatasets = createAsyncThunk(
  "metrics/fetchAdminDatasets",
  async (
    params: { organization_id: string } | Record<string, unknown> | undefined,
    { rejectWithValue }
  ) => {
    try {
      const response = await get(API_URLS.adminMetricsDatasets(params));
      return response?.content ?? response ?? {};
    } catch (error: any) {
      return rejectWithValue(
        toErrorMessage(
          error?.response?.data || error?.message,
          "Failed to load metrics datasets"
        )
      );
    }
  }
);

const MetricsSlice = createSlice({
  name: "metrics",
  initialState,
  reducers: {
    clearMetricsCsvValidateState: state => {
      state.csvValidate.loading = false;
      state.csvValidate.error = null;
      state.csvValidate.success = false;
      state.csvValidate.data = null;
    },
    clearMetricsCsvImportState: state => {
      state.csvImport.loading = false;
      state.csvImport.error = null;
      state.csvImport.success = false;
      state.csvImport.data = null;
    },
    clearMetricsStagingTransferState: state => {
      state.stagingTransfer.loading = false;
      state.stagingTransfer.error = null;
      state.stagingTransfer.success = false;
      state.stagingTransfer.data = null;
    },
    clearMetricsBenchmarkCalculateState: state => {
      state.benchmarkCalculate.loading = false;
      state.benchmarkCalculate.error = null;
      state.benchmarkCalculate.success = false;
      state.benchmarkCalculate.data = null;
    },
    clearMetricsBenchmarkPropagateState: state => {
      state.benchmarkPropagate.loading = false;
      state.benchmarkPropagate.error = null;
      state.benchmarkPropagate.success = false;
      state.benchmarkPropagate.data = null;
    },
    clearMetricsNormalizationState: state => {
      state.normalization.loading = false;
      state.normalization.error = null;
      state.normalization.success = false;
      state.normalization.data = null;
    },
    clearMetricsNormalizationPendingCountState: state => {
      state.normalizationPendingCount.loading = false;
      state.normalizationPendingCount.error = null;
      state.normalizationPendingCount.success = false;
      state.normalizationPendingCount.count = 0;
    },
    clearMetricsStagingCountState: state => {
      state.stagingCount.loading = false;
      state.stagingCount.error = null;
      state.stagingCount.success = false;
      state.stagingCount.count = 0;
    },
    clearMetricsOrgConfigurationUpdateState: state => {
      state.orgConfigurationUpdate.loading = false;
      state.orgConfigurationUpdate.error = null;
      state.orgConfigurationUpdate.success = false;
    },
    clearMetricsSummaryStatusState: state => {
      state.summaryStatus.loading = false;
      state.summaryStatus.error = null;
      state.summaryStatus.success = false;
      state.summaryStatus.data = null;
    },
    clearMetricsSummaryTransferState: state => {
      state.summaryTransfer.loading = false;
      state.summaryTransfer.error = null;
      state.summaryTransfer.success = false;
      state.summaryTransfer.data = null;
    },
  },
  extraReducers: builder => {
    builder.addCase(validateMetricsCsv.pending, state => {
      state.csvValidate.loading = true;
      state.csvValidate.error = null;
      state.csvValidate.success = false;
    });

    builder.addCase(validateMetricsCsv.fulfilled, (state, action) => {
      state.csvValidate.loading = false;
      state.csvValidate.error = null;
      state.csvValidate.success = true;
      state.csvValidate.data = action.payload;
    });

    builder.addCase(validateMetricsCsv.rejected, (state, action) => {
      state.csvValidate.loading = false;
      state.csvValidate.error = (action.payload as string) || null;
      state.csvValidate.success = false;
      state.csvValidate.data = null;
    });

    builder.addCase(importMetricsCsv.pending, state => {
      state.csvImport.loading = true;
      state.csvImport.error = null;
      state.csvImport.success = false;
    });

    builder.addCase(importMetricsCsv.fulfilled, (state, action) => {
      state.csvImport.loading = false;
      state.csvImport.error = null;
      state.csvImport.success = true;
      state.csvImport.data = action.payload;
      toast.success(
        action.payload?.processResponse?.response?.message ||
          action.payload?.processResponse?.message ||
          "CSV imported and staging process started."
      );
    });

    builder.addCase(importMetricsCsv.rejected, (state, action) => {
      state.csvImport.loading = false;
      state.csvImport.success = false;
      state.csvImport.error =
        (action.payload as string) || "Failed to import metrics CSV";
      toast.error((action.payload as string) || "Failed to import metrics CSV");
    });

    builder.addCase(transferMetricsStagingToProduction.pending, state => {
      state.stagingTransfer.loading = true;
      state.stagingTransfer.error = null;
      state.stagingTransfer.success = false;
    });

    builder.addCase(
      transferMetricsStagingToProduction.fulfilled,
      (state, action) => {
        state.stagingTransfer.loading = false;
        state.stagingTransfer.error = null;
        state.stagingTransfer.success = true;
        state.stagingTransfer.data = action.payload;
        toast.success(
          action.payload?.message || "Staging data transferred to production."
        );
      }
    );

    builder.addCase(
      transferMetricsStagingToProduction.rejected,
      (state, action) => {
        state.stagingTransfer.loading = false;
        state.stagingTransfer.success = false;
        state.stagingTransfer.error =
          (action.payload as string) ||
          "Failed to transfer staging data to production";
        toast.error(
          (action.payload as string) ||
            "Failed to transfer staging data to production"
        );
      }
    );

    builder.addCase(fetchMetricsStagingCount.pending, state => {
      state.stagingCount.loading = true;
      state.stagingCount.error = null;
      state.stagingCount.success = false;
    });

    builder.addCase(fetchMetricsStagingCount.fulfilled, (state, action) => {
      state.stagingCount.loading = false;
      state.stagingCount.error = null;
      state.stagingCount.success = true;
      state.stagingCount.count = action.payload;
    });

    builder.addCase(fetchMetricsStagingCount.rejected, (state, action) => {
      state.stagingCount.loading = false;
      state.stagingCount.success = false;
      state.stagingCount.error =
        (action.payload as string) || "Failed to load staging count";
      state.stagingCount.count = 0;
    });

    builder.addCase(fetchMetricsOrgConfiguration.pending, state => {
      state.orgConfiguration.loading = true;
      state.orgConfiguration.error = null;
      state.orgConfiguration.success = false;
    });

    builder.addCase(fetchMetricsOrgConfiguration.fulfilled, (state, action) => {
      state.orgConfiguration.loading = false;
      state.orgConfiguration.error = null;
      state.orgConfiguration.success = true;
      state.orgConfiguration.data = action.payload;
    });

    builder.addCase(fetchMetricsOrgConfiguration.rejected, (state, action) => {
      state.orgConfiguration.loading = false;
      state.orgConfiguration.success = false;
      state.orgConfiguration.error =
        (action.payload as string) || "Failed to load metrics configuration";
      state.orgConfiguration.data = null;
    });

    builder.addCase(updateMetricsOrgConfiguration.pending, state => {
      state.orgConfigurationUpdate.loading = true;
      state.orgConfigurationUpdate.error = null;
      state.orgConfigurationUpdate.success = false;
    });

    builder.addCase(
      updateMetricsOrgConfiguration.fulfilled,
      (state, action) => {
        state.orgConfigurationUpdate.loading = false;
        state.orgConfigurationUpdate.error = null;
        state.orgConfigurationUpdate.success = true;
        if (!action.meta.arg?.config?.silent) {
          toast.success(
            action.payload?.response?.message ||
              action.payload?.message ||
              "Configuration updated successfully."
          );
        }
      }
    );

    builder.addCase(updateMetricsOrgConfiguration.rejected, (state, action) => {
      state.orgConfigurationUpdate.loading = false;
      state.orgConfigurationUpdate.success = false;
      state.orgConfigurationUpdate.error =
        (action.payload as string) || "Failed to update metrics configuration";
      toast.error(
        (action.payload as string) || "Failed to update metrics configuration"
      );
    });

    builder.addCase(transferMetricsSummary.pending, state => {
      state.summaryTransfer.loading = true;
      state.summaryTransfer.error = null;
      state.summaryTransfer.success = false;
    });

    builder.addCase(transferMetricsSummary.fulfilled, (state, action) => {
      state.summaryTransfer.loading = false;
      state.summaryTransfer.error = null;
      state.summaryTransfer.success = true;
      state.summaryTransfer.data = action.payload;
      toast.success(
        action.payload?.content?.message ||
          action.payload?.message ||
          "The summaries have been refreshed."
      );
    });

    builder.addCase(transferMetricsSummary.rejected, (state, action) => {
      state.summaryTransfer.loading = false;
      state.summaryTransfer.success = false;
      state.summaryTransfer.error =
        (action.payload as string) || "Failed to refresh metric summaries";
      toast.error(
        (action.payload as string) || "Failed to refresh metric summaries"
      );
    });

    builder.addCase(fetchMetricsSummaryStatus.pending, state => {
      state.summaryStatus.loading = true;
      state.summaryStatus.error = null;
      state.summaryStatus.success = false;
    });

    builder.addCase(fetchMetricsSummaryStatus.fulfilled, (state, action) => {
      state.summaryStatus.loading = false;
      state.summaryStatus.error = null;
      state.summaryStatus.success = true;
      state.summaryStatus.data = action.payload;
    });

    builder.addCase(fetchMetricsSummaryStatus.rejected, (state, action) => {
      state.summaryStatus.loading = false;
      state.summaryStatus.success = false;
      state.summaryStatus.error =
        (action.payload as string) || "Failed to load summary status";
      state.summaryStatus.data = null;
    });

    builder.addCase(calculateMetricsBenchmarks.pending, state => {
      state.benchmarkCalculate.loading = true;
      state.benchmarkCalculate.error = null;
      state.benchmarkCalculate.success = false;
    });

    builder.addCase(calculateMetricsBenchmarks.fulfilled, (state, action) => {
      state.benchmarkCalculate.loading = false;
      state.benchmarkCalculate.error = null;
      state.benchmarkCalculate.success = true;
      state.benchmarkCalculate.data = action.payload;
      const msg = action.payload?.message;
      toast.success(
        typeof msg === "string" && msg.trim()
          ? msg
          : "Benchmarks calculated successfully."
      );
    });

    builder.addCase(calculateMetricsBenchmarks.rejected, (state, action) => {
      state.benchmarkCalculate.loading = false;
      state.benchmarkCalculate.success = false;
      state.benchmarkCalculate.error =
        (action.payload as string) || "Failed to calculate benchmarks";
      toast.error(
        (action.payload as string) || "Failed to calculate benchmarks"
      );
    });

    builder.addCase(propagateMetricsBenchmarks.pending, state => {
      state.benchmarkPropagate.loading = true;
      state.benchmarkPropagate.error = null;
      state.benchmarkPropagate.success = false;
    });

    builder.addCase(propagateMetricsBenchmarks.fulfilled, (state, action) => {
      state.benchmarkPropagate.loading = false;
      state.benchmarkPropagate.error = null;
      state.benchmarkPropagate.success = true;
      state.benchmarkPropagate.data = action.payload;
      toast.success(
        action.payload?.response?.message ||
          action.payload?.message ||
          "Benchmarks propagated successfully."
      );
    });

    builder.addCase(propagateMetricsBenchmarks.rejected, (state, action) => {
      state.benchmarkPropagate.loading = false;
      state.benchmarkPropagate.success = false;
      state.benchmarkPropagate.error =
        (action.payload as string) || "Failed to propagate benchmarks";
      toast.error(
        (action.payload as string) || "Failed to propagate benchmarks"
      );
    });

    builder.addCase(normalizeMetrics.pending, state => {
      state.normalization.loading = true;
      state.normalization.error = null;
      state.normalization.success = false;
    });

    builder.addCase(normalizeMetrics.fulfilled, (state, action) => {
      state.normalization.loading = false;
      state.normalization.error = null;
      state.normalization.success = true;
      state.normalization.data = action.payload;
      toast.success(
        action.payload?.response?.message ||
          action.payload?.message ||
          "Metrics normalized successfully."
      );
    });

    builder.addCase(normalizeMetrics.rejected, (state, action) => {
      state.normalization.loading = false;
      state.normalization.success = false;
      state.normalization.error =
        (action.payload as string) || "Failed to normalize metrics";
      toast.error((action.payload as string) || "Failed to normalize metrics");
    });

    builder.addCase(fetchMetricsNormalizePendingCount.pending, state => {
      state.normalizationPendingCount.loading = true;
      state.normalizationPendingCount.error = null;
      state.normalizationPendingCount.success = false;
    });

    builder.addCase(
      fetchMetricsNormalizePendingCount.fulfilled,
      (state, action) => {
        state.normalizationPendingCount.loading = false;
        state.normalizationPendingCount.error = null;
        state.normalizationPendingCount.success = true;
        state.normalizationPendingCount.count = action.payload;
      }
    );

    builder.addCase(
      fetchMetricsNormalizePendingCount.rejected,
      (state, action) => {
        state.normalizationPendingCount.loading = false;
        state.normalizationPendingCount.success = false;
        state.normalizationPendingCount.error =
          (action.payload as string) ||
          "Failed to load pending normalization count";
        state.normalizationPendingCount.count = 0;
      }
    );

    builder.addCase(fetchMetricsTimePeriods.pending, state => {
      state.timePeriods.loading = true;
      state.timePeriods.error = null;
      state.timePeriods.success = false;
    });
    builder.addCase(fetchMetricsTimePeriods.fulfilled, (state, action) => {
      state.timePeriods.loading = false;
      state.timePeriods.error = null;
      state.timePeriods.success = true;
      state.timePeriods.data = action.payload;
    });
    builder.addCase(fetchMetricsTimePeriods.rejected, (state, action) => {
      state.timePeriods.loading = false;
      state.timePeriods.success = false;
      state.timePeriods.error = (action.payload as string) || null;
      state.timePeriods.data = null;
    });

    builder.addCase(fetchMetricsConfigCoverageAudit.pending, state => {
      state.coverageAudit.loading = true;
      state.coverageAudit.error = null;
      state.coverageAudit.success = false;
    });
    builder.addCase(
      fetchMetricsConfigCoverageAudit.fulfilled,
      (state, action) => {
        state.coverageAudit.loading = false;
        state.coverageAudit.error = null;
        state.coverageAudit.success = true;
        state.coverageAudit.data = action.payload;
      }
    );
    builder.addCase(
      fetchMetricsConfigCoverageAudit.rejected,
      (state, action) => {
        state.coverageAudit.loading = false;
        state.coverageAudit.success = false;
        state.coverageAudit.error = (action.payload as string) || null;
        state.coverageAudit.data = null;
      }
    );

    builder.addCase(fetchHawkeyeDigest.pending, state => {
      state.hawkeyeDigest.loading = true;
      state.hawkeyeDigest.error = null;
      state.hawkeyeDigest.success = false;
    });
    builder.addCase(fetchHawkeyeDigest.fulfilled, (state, action) => {
      state.hawkeyeDigest.loading = false;
      state.hawkeyeDigest.error = null;
      state.hawkeyeDigest.success = true;
      state.hawkeyeDigest.data = action.payload;
    });
    builder.addCase(fetchHawkeyeDigest.rejected, (state, action) => {
      state.hawkeyeDigest.loading = false;
      state.hawkeyeDigest.success = false;
      state.hawkeyeDigest.error = (action.payload as string) || null;
      state.hawkeyeDigest.data = null;
    });

    builder.addCase(fetchAdminMetricsDatasets.pending, state => {
      state.adminDatasets.loading = true;
      state.adminDatasets.error = null;
      state.adminDatasets.success = false;
    });
    builder.addCase(fetchAdminMetricsDatasets.fulfilled, (state, action) => {
      state.adminDatasets.loading = false;
      state.adminDatasets.error = null;
      state.adminDatasets.success = true;
      state.adminDatasets.data = action.payload;
    });
    builder.addCase(fetchAdminMetricsDatasets.rejected, (state, action) => {
      state.adminDatasets.loading = false;
      state.adminDatasets.success = false;
      state.adminDatasets.error = (action.payload as string) || null;
      state.adminDatasets.data = null;
    });
  },
});

export const {
  clearMetricsCsvValidateState,
  clearMetricsCsvImportState,
  clearMetricsStagingTransferState,
  clearMetricsBenchmarkCalculateState,
  clearMetricsBenchmarkPropagateState,
  clearMetricsNormalizationState,
  clearMetricsNormalizationPendingCountState,
  clearMetricsStagingCountState,
  clearMetricsOrgConfigurationUpdateState,
  clearMetricsSummaryStatusState,
  clearMetricsSummaryTransferState,
} = MetricsSlice.actions;
export default MetricsSlice.reducer;
